
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, CreditCard as CreditCardIcon, Wallet, ArrowRight, Loader2, Lock, ChevronLeft, Sparkles, Wifi } from 'lucide-react';
import { Tier } from '../../types/tiers';
import { Button } from '../ui/Button';
import { ChainIcon } from '../ui/ChainIcons';
import { fetchTokenPrice } from '../../services/prices';

const GHOST_LOGO_URL = "https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg";

// --- High Fidelity Icons ---

const MastercardIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <circle cx="7" cy="12" r="7" fill="currentColor" fillOpacity="0.8"/>
        <circle cx="17" cy="12" r="7" fill="currentColor" fillOpacity="0.5"/>
    </svg>
);

const VisaIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M18.534 32.784L21.418 14.86H25.352L22.468 32.784H18.534ZM34.792 32.184C34.394 32.358 33.722 32.532 32.906 32.532C30.826 32.532 29.354 31.42 29.354 28.536V19.92H25.96V18.272L29.354 17.616V14.224L33.288 13.568V17.616H36.32V21.168H33.288V28.008C33.288 29.328 33.528 29.952 34.328 29.952C34.608 29.952 34.848 29.904 35.048 29.908L34.792 32.184ZM15.698 32.784L12.722 20.352L12.386 18.672C11.954 17.04 11.858 16.704 10.37 15.936C9.554 15.504 8.786 15.12 8.018 14.784L8.162 14.224H16.274L17.858 24.384L21.314 14.224H25.154L19.49 32.784H15.698ZM41.872 32.784H37.938L40.822 14.86H44.756L41.872 32.784Z" fill="currentColor"/>
    </svg>
);

// --- Sophisticated Card Visual ---

const CreditCardVisual: React.FC<{ cardName?: string; cardNumber?: string; expiry?: string }> = ({ 
    cardName = "ALEXANDER GHOST", 
    cardNumber = "•••• •••• •••• 4242", 
    expiry = "12/28" 
}) => (
    <div className="relative w-full aspect-[1.586/1] rounded-md overflow-hidden shadow-2xl transition-all duration-700 hover:scale-[1.02] group select-none border border-white/10">
        <div className="absolute inset-0 bg-[#0A0B0F]">
            {/* Real Brushed Metal Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1F2128] via-[#0A0B0F] to-[#15171C]"></div>
            <div className="absolute inset-0 opacity-[0.08] bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')] mix-blend-overlay"></div>
            
            {/* Subtle glow from logo */}
            <div className="absolute -right-8 -bottom-8 w-48 h-48 opacity-[0.04] rotate-12 pointer-events-none">
                 <img src={GHOST_LOGO_URL} alt="" className="w-full h-full object-contain invert" />
            </div>
            
            {/* Internal shadow for depth */}
            <div className="absolute inset-0 shadow-[inset_0_2px_15px_rgba(0,0,0,0.8)]"></div>
        </div>

        <div className="relative z-10 h-full p-8 flex flex-col justify-between text-white font-sans">
            <div className="flex justify-between items-start">
                <div className="w-12 h-8 rounded-[4px] bg-gradient-to-br from-[#E8C07D] via-[#C5A059] to-[#8A6D3B] border border-black/20 shadow-lg relative overflow-hidden">
                    <div className="absolute top-1/2 left-0 right-0 h-[0.5px] bg-black/30"></div>
                </div>
                <div className="flex items-center gap-4 text-white/40">
                    <Wifi size={14} className="rotate-90 opacity-60" />
                    <div className="flex items-center gap-3">
                         <VisaIcon className="h-3 w-auto opacity-80" />
                         <MastercardIcon className="h-4 w-auto opacity-80" />
                    </div>
                </div>
            </div>

            <div className="text-xl sm:text-2xl font-mono tracking-[0.25em] text-white/95 drop-shadow-2xl text-center my-4 font-bold">
                {cardNumber || "•••• •••• •••• 0000"}
            </div>

            <div className="flex justify-between items-end border-t border-white/5 pt-6">
                <div className="space-y-1 min-w-0">
                    <div className="text-[6px] uppercase tracking-[0.4em] font-black text-stone-500">Sovereign Holder</div>
                    <div className="text-[11px] font-bold tracking-widest uppercase text-white/90 truncate">
                        {cardName || "IDENTITY VERIFIED"}
                    </div>
                </div>
                <div className="flex flex-col items-end">
                    <div className="text-[6px] uppercase tracking-[0.4em] font-black text-stone-500">Expiry</div>
                    <div className="text-[11px] font-mono font-bold text-white/90 tracking-widest">{expiry || "MM/YY"}</div>
                </div>
            </div>
        </div>
        
        {/* Beveled Edge Highlight */}
        <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-md pointer-events-none"></div>
    </div>
);

// --- Optimized Components ---

const PaymentOption = ({ active, onClick, icon: Icon, title, desc }: any) => (
    <button 
        onClick={onClick}
        className={`w-full flex items-center gap-4 p-5 rounded-sm border transition-all duration-300 ${
            active 
            ? 'bg-stone-900 dark:bg-white border-transparent text-white dark:text-black shadow-xl scale-[1.02] z-10' 
            : 'bg-white dark:bg-white/5 border-stone-200 dark:border-white/10 text-stone-500 hover:border-stone-300 dark:hover:border-white/20'
        }`}
    >
        <div className={`p-2.5 rounded-sm ${active ? 'bg-white/10 dark:bg-black/5 text-nobel-gold' : 'bg-stone-100 dark:bg-white/5 text-stone-400'}`}>
            <Icon size={22} />
        </div>
        <div className="text-left">
            <div className="font-bold text-sm uppercase tracking-widest">{title}</div>
            <div className={`text-[10px] uppercase font-bold opacity-60 ${active ? 'text-stone-300 dark:text-stone-600' : 'text-stone-400'}`}>{desc}</div>
        </div>
        {active && <ArrowRight size={18} className="ml-auto" />}
    </button>
);

export const PlanCheckoutModal: React.FC<{
    tier: Tier;
    cycle: string;
    price: number;
    onClose: () => void;
    onSuccess: () => void;
}> = ({ tier, cycle, price, onClose, onSuccess }) => {
    const [method, setMethod] = useState<'crypto' | 'card' | null>(null);
    const [chain, setChain] = useState<'SOL' | 'SUI'>('SOL');
    const [step, setStep] = useState<'select' | 'details' | 'processing' | 'success'>('select');
    const [exchangeRate, setExchangeRate] = useState<number>(1);
    
    const [cardName, setCardName] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [cardExpiry, setCardExpiry] = useState("");

    useEffect(() => {
        if (step === 'select' || step === 'details') {
            fetchTokenPrice(chain as any).then(rate => rate && setExchangeRate(rate));
        }
    }, [chain, step]);

    const cryptoAmount = price / exchangeRate;

    const handleAction = async () => {
        setStep('processing');
        await new Promise(res => setTimeout(res, 2500));
        setStep('success');
    };

    const handleSelectMethod = (m: 'crypto' | 'card') => {
        setMethod(m);
        setStep('details');
    };

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-md"
            onClick={onClose}
        >
            <motion.div 
                initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
                className="bg-white dark:bg-[#15171C] rounded-sm shadow-2xl w-full max-w-[460px] overflow-hidden border border-stone-200 dark:border-[#2D3142] relative flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-8 pb-4 flex justify-between items-center">
                    {step === 'details' && (
                        <button onClick={() => setStep('select')} className="p-2 -ml-2 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div className="flex-1 text-center">
                        <div className="text-[10px] font-black text-stone-400 uppercase tracking-[0.3em] mb-1">Ghost Subscriptions</div>
                        <h2 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white">{tier} Plan</h2>
                    </div>
                    <button onClick={onClose} className="p-2 -mr-2 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-8 pt-4 space-y-8 flex-1">
                    <AnimatePresence mode="wait">
                        {step === 'select' && (
                            <motion.div key="select" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="space-y-4">
                                <div className="p-6 bg-stone-50 dark:bg-white/[0.03] rounded-sm border border-stone-100 dark:border-white/5 flex justify-between items-end">
                                    <div>
                                        <div className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-1">Total Due</div>
                                        <div className="text-3xl font-serif font-bold text-[#0A0B0F] dark:text-white tracking-tighter">${price.toLocaleString()}</div>
                                    </div>
                                    <div className="text-[9px] font-black text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-sm border border-emerald-500/20 uppercase tracking-widest">
                                        {cycle} billing
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <PaymentOption 
                                        onClick={() => handleSelectMethod('crypto')}
                                        icon={Wallet} title="On-Chain Assets" desc="SOL / SUI / USDC (Instant)" 
                                    />
                                    <PaymentOption 
                                        onClick={() => handleSelectMethod('card')}
                                        icon={CreditCardIcon} title="Titanium Card" desc="Standard Credit / Debit" 
                                    />
                                </div>
                            </motion.div>
                        )}

                        {step === 'details' && method === 'crypto' && (
                            <motion.div key="crypto" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    {(['SOL', 'SUI'] as const).map(c => (
                                        <button 
                                            key={c} onClick={() => setChain(c)}
                                            className={`p-4 rounded-sm border transition-all ${chain === c ? 'bg-stone-50 dark:bg-white/5 border-nobel-gold shadow-lg ring-4 ring-nobel-gold/5' : 'bg-white dark:bg-white/[0.02] border-stone-200 dark:border-white/10 opacity-50'}`}
                                        >
                                            <ChainIcon chain={c} className="w-8 h-8 mx-auto mb-2" />
                                            <div className="text-center font-black text-xs uppercase tracking-widest dark:text-white">{c} Network</div>
                                        </button>
                                    ))}
                                </div>
                                <div className="p-6 bg-stone-900 text-white rounded-sm border border-white/10 text-center space-y-2">
                                    <div className="text-[10px] font-black text-stone-500 uppercase tracking-widest">Settlement Amount</div>
                                    <div className="text-3xl font-mono font-bold text-nobel-gold">{cryptoAmount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {chain}</div>
                                    <div className="text-[9px] text-stone-600 font-bold uppercase tracking-widest">1 {chain} ≈ ${exchangeRate.toFixed(2)}</div>
                                </div>
                                <Button onClick={handleAction} variant="secondary" className="w-full h-16 rounded-sm text-base shadow-2xl font-black uppercase tracking-widest">Authorize Payment</Button>
                            </motion.div>
                        )}

                        {step === 'details' && method === 'card' && (
                            <motion.div key="card" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                                <div className="flex justify-center">
                                    <CreditCardVisual cardName={cardName} cardNumber={cardNumber} expiry={cardExpiry} />
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    <input 
                                        type="text" placeholder="CARDHOLDER NAME" value={cardName} onChange={e => setCardName(e.target.value.toUpperCase())}
                                        className="w-full p-4 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-sm text-xs font-black uppercase tracking-widest outline-none focus:border-nobel-gold transition-all dark:text-white"
                                    />
                                    <input 
                                        type="text" placeholder="0000 0000 0000 0000" value={cardNumber} onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').substring(0, 16).match(/.{1,4}/g)?.join(' ') || '')}
                                        className="w-full p-4 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-sm text-xs font-black tracking-[0.3em] outline-none focus:border-nobel-gold transition-all dark:text-white"
                                    />
                                    <div className="grid grid-cols-2 gap-4">
                                        <input 
                                            type="text" placeholder="MM / YY" value={cardExpiry} onChange={e => setCardExpiry(e.target.value.replace(/\D/g, '').substring(0, 4).replace(/(\d{2})(\d{2})/, '$1/$2'))}
                                            className="w-full p-4 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-sm text-xs font-black tracking-widest outline-none focus:border-nobel-gold transition-all dark:text-white"
                                        />
                                        <input 
                                            type="password" placeholder="CVC" maxLength={3}
                                            className="w-full p-4 bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-sm text-xs font-black tracking-[0.5em] outline-none focus:border-nobel-gold transition-all dark:text-white"
                                        />
                                    </div>
                                </div>
                                <Button onClick={handleAction} variant="secondary" className="w-full h-16 rounded-sm text-base shadow-2xl font-black uppercase tracking-widest">Sign Transaction</Button>
                            </motion.div>
                        )}

                        {step === 'processing' && (
                            <motion.div key="proc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-20 flex flex-col items-center justify-center text-center space-y-8">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full border border-nobel-gold/20 animate-ping"></div>
                                    <Loader2 size={64} className="text-nobel-gold animate-spin" />
                                </div>
                                <div>
                                    <h3 className="font-serif text-2xl font-bold text-[#0A0B0F] dark:text-white mb-2">Broadcasting to Mainnet</h3>
                                    <p className="text-[10px] text-stone-500 uppercase font-black tracking-[0.3em]">Zero-Knowledge Relay Active</p>
                                </div>
                            </motion.div>
                        )}

                        {step === 'success' && (
                            <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-12 flex flex-col items-center justify-center text-center space-y-8">
                                <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/20">
                                    <CheckCircle size={40} className="text-white" strokeWidth={3} />
                                </div>
                                <div className="space-y-2">
                                    <h3 className="text-3xl font-serif font-bold text-[#0A0B0F] dark:text-white tracking-tight">Access Granted</h3>
                                    <p className="text-stone-500 text-sm leading-relaxed max-w-xs mx-auto">Your Ghost {tier} session is now active. All enterprise rails are provisioned.</p>
                                </div>
                                <Button onClick={onSuccess} variant="secondary" className="w-full h-14 rounded-sm text-xs font-black uppercase tracking-[0.2em] shadow-xl">Enter GhostOS</Button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="p-6 bg-stone-50 dark:bg-black/20 text-center border-t border-stone-100 dark:border-white/5">
                    <p className="text-[9px] text-stone-400 font-black uppercase tracking-[0.4em] flex items-center justify-center gap-2">
                        <Lock size={10} className="text-emerald-500" /> AES-256 Encrypted Session
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
};
